import Model from './model'
import ProjectMeta from '@/models/project/meta'
import { isEmpty } from 'lodash'
import formatTime from '@/utils/time'

class Project extends Model {

  constructor(project) {
    super()
    this.id = project.id
    this.serial = project.serial
    this.livesync_since = project.livesync_since || 30
    this.published_url = project.published_url
    this.sheet_url = project.sheet_url
    this.sheet_name = project.sheet_name
    this.name = project.name
    this.owner = project.owner
    this.galleries = project.galleries
    this.meta = new ProjectMeta(project.meta)
    this.blocks = project.blocks
    this.publish_at = project.publish_at
    this.published_at = project.published_at
    this.updated_at = project.updated_at
    this.created_at = project.created_at
  }

  // TODO: 需要更有彈性的驗證機制
  get isValid() {
    return this.meta.isValid
  }

  /**
   * 發佈日期加上 livesync_since 天數格式化後的日期格式
   *
   * @return {String}
   */
  get livesyncStamps() {
    if (!this.isPublished) {
      return null
    }

    const days = 24 * 60 * 60 * 1000
    const publishedAt = new Date(this.published_at)
    const livesyncAt = new Date()

    livesyncAt.setTime(publishedAt.getTime() + (this.livesync_since * days))

    return formatTime(livesyncAt)
  }

  /**
   * 檢查專案是否已經發佈，如果有發佈 published_at 應該會是空字串
   *
   * @return {Boolean}
   */
  get isPublished() {
    return !isEmpty(this.published_at)
  }

  /**
   * 檢查專案是否有更新但是還沒有發佈
   *
   * @return {Boolean}
   */
  get isExpiredAfterPublished() {
    return this.isPublished && (new Date(this.updated_at) > new Date(this.published_at))
  }

  //
  //
  // Static Methods
  // ================================================================

  /**
   * Get project by a given id.
   *
   * @param  {Number} id  The id of the project.
   * @return {Promise}
   */
  static get(id) {
    return this.api
      .get(`projects/${id}`)
      .then(res => res.data)
  }

  /**
   * Get Projects by a list of ids.
   *
   * @param  {Array} ids  An array of ids.
   * @return {Promise}
   */
  static where(ids) {
    return this.api
      .get('projects', { params: { ids: ids.join(',') } })
      .then(res => res.data)
  }

  /**
   * 依照排序類型、排序規則，取得專案的 ids list。
   *
   * @param  {String} options.type      Available: `index`, `published`, `unpublished`, `created_by_me`, default is `index`.
   * @param  {String} options.sort_type Available: `name`, `created_at`, `updated_at`, `serial`, `published_at`, `created_at`
   * @param  {String} options.sort_by   Available: `desc`, `asc`
   * @param  {String} options.name      Available: '專案名稱'
   * @param  {String} options.serial      Available: '發報碼'
   * @return {Promise}
   */
  static list({ type, sort_type, sort_by, name, serial }) {
    return this.api
      .get('projects/list', { params: { type, sort_type, sort_by, name, serial } })
      .then(res => res.data)
  }

  /**
   * Tries to create a new project record.
   *
   * @param  {String} options.name         專案名稱
   * @param  {Array} options.galleries     專案所屬的管別 ID 陣列，ex [3, 8, 12]
   * @param  {String} options.serial       發報碼
   * @param  {String} options.sheet_url    Google 試算表網址
   * @param  {String} options.sheet_name   試算表分頁名稱
   * @return {Promise}
   */
  static create({ name, galleries, serial, sheet_url, sheet_name }) {
    return this.api
      .post('projects', { name, galleries, serial, sheet_url, sheet_name })
      .then(res => res.data)
  }

  static copy({ id, name, serial }) {
    return this.api
      .post(`projects/${id}/copy`, { name, serial })
      .then(res => res.data)
  }

  /**
   * Updates the project by given project id.
   *
   * @param  {Number} id     The id of the project
   * @param  {Object} data   The project attributes
   * @return {Promise}
   */
  static update(id, data) {
    return this.api
      .patch(`projects/${id}`, data)
      .then(res => res.data)
  }

  /**
   * Tries to delete projects that have given ids of projects.
   *
   * @param  {Array} ids   The ids of projects.
   * @return {Promise}
   */
  static delete(ids) {
    return this.api
      .delete('projects', { params: { ids: ids.join(',') } })
  }

  /**
   * Get the preview result(HTML) by the given project id and previewing type.
   *
   * @param  {Number} id     The project id
   * @param  {String} type   previewing type
   * @return {Promise}
   */
  static preview(id, type) {
    return this.api
      .get(`projects/${id}/preview`, {
        headers: {
          Accept: 'text/html'
        },
        params: { type }
      })
  }

  /**
   * Send a request to server sync product data
   *
   * @param  {Number} id    The project id
   * @return {Promise}
   */
  static sync(id) {
    return this.api
      .post(`projects/${id}/sync`)
      .then(res => res.data)
  }

  /**
   * Download the EDM HTML from server.
   *
   * @param  {Number} id   The project id.
   * @return {Promise}
   */
  static download(id) {
    return this.api
      .get(`projects/${id}/download`, {
        responseType: 'blob'
      })
      .then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        const fileName = getResponseFileName(res)
        link.href = url
        // follwing the file naming rule from server, but gives it a fallback.
        link.setAttribute('download', fileName || `edm-${Date.now()}.html`)
        document.body.appendChild(link)
        link.click()
        // remember to revoke ObjectURL object prevent memory leaks.
        window.URL.revokeObjectURL(url)
      })
  }

  /**
   * Request server to publish the project by the given project id.
   *
   * @param  {Number} id  The project id.
   * @return {Promise}
   */
  static publish(id) {
    return this.api
      .post(`projects/${id}/publish`)
      .then(res => res.data)
  }

}

function getResponseFileName(res) {
  return res.headers['content-disposition'].match(/filename="(.+)"/)[1]
}

export default Project



// WEBPACK FOOTER //
// ./src/models/project.js